import { doc, setDoc, deleteDoc, collection, getDocs } from 'firebase/firestore';
import { firestore } from '../firebase/config';
import { isVariant } from '../utils';

/**
 * Sanitize a series title for use as a Firestore document path segment.
 * Forward slashes are replaced with %2F because Firestore treats '/' as a path separator.
 */
export const sanitizeSeriesTitle = (title) => {
  if (!title) return title;
  return title.replace(/\//g, '%2F');
};

/**
 * Decode a sanitized series title back to its display form.
 * Reverses the encoding applied by sanitizeSeriesTitle.
 */
export const displaySeriesTitle = (title) => {
  if (!title) return title;
  return title.replace(/%2F/g, '/');
};

/**
 * Format a series title to include the start year in parentheses, e.g. "Spider-Man (2022)".
 * Returns the title unchanged if startYear is missing, invalid, or already present.
 */
export const formatSeriesTitle = (title, startYear) => {
  if (!startYear) return title;
  if (/\(\d{4}\)/.test(title)) return title; // already has a year
  const yearStr = String(startYear);
  if (!/^\d{4}$/.test(yearStr)) return title; // not a valid 4-digit year
  return `${title} (${yearStr})`;
};

/**
 * Search a hero's series collection for any entry whose comicVineId matches.
 * Returns the series data object or null if not found.
 */
export const findSeriesByComicVineId = async (comicVineId, heroName, userEmail) => {
  if (!comicVineId) return null;
  const seriesRef = collection(firestore, "users/" + userEmail + "/heroes/" + heroName + "/series");
  const querySnapshot = await getDocs(seriesRef);
  for (const docSnap of querySnapshot.docs) {
    const seriesData = docSnap.data()?.data;
    if (seriesData && seriesData.comicVineId === comicVineId) {
      return seriesData;
    }
  }
  return null;
};

/**
 * Delete a series from Firebase
 */
export const deleteSeriesFromFirebase = async (seriesCard, hero, userEmail) => {
  const seriesTitle = sanitizeSeriesTitle(seriesCard.title || seriesCard.id);
  const userData = doc(firestore, "users/" + userEmail + "/heroes/" + hero.name + "/series/" + seriesTitle);

  // Delete all comics in this series
  const comicsCollection = collection(firestore, "users/" + userEmail + "/heroes/" + hero.name + "/series/" + seriesTitle + "/comics");
  const querySnapshot = await getDocs(comicsCollection);

  const promises = querySnapshot.docs.map(comic => deleteDoc(comic.ref));
  await Promise.all(promises);

  await deleteDoc(userData);
};

/**
 * Save comics for a series to Firebase
 */
export const saveComicsToFirebase = async (comics, series, hero, userEmail) => {
  // Group comics by issue number
  const comicsByIssue = {};
  comics.forEach(element => {
    if (!comicsByIssue[element.issueNumber]) {
      comicsByIssue[element.issueNumber] = {
        main: null,
        variants: []
      };
    }

    if (isVariant(element)) {
      comicsByIssue[element.issueNumber].variants.push(element);
    } else {
      comicsByIssue[element.issueNumber].main = element;
    }
  });

  // Save each issue with its variants
  const promises = [];
  const formattedTitle = sanitizeSeriesTitle(formatSeriesTitle(series.title, series.startYear));

  Object.keys(comicsByIssue).forEach(issueNumber => {
    const issueData = comicsByIssue[issueNumber];
    const mainComic = issueData.main || (issueData.variants.length > 0 ? issueData.variants[0] : null);

    if (!mainComic) return;

    const seriesImageName = series.thumbnail.path.split('/').pop();
    const seriesImage = mainComic.issueNumber === 1 && seriesImageName === 'image_not_available'
      ? mainComic.thumbnail.path
      : series.thumbnail.path;

    const seriesData = { ...series, title: formattedTitle, thumbnail: { ...series.thumbnail, path: seriesImage } };

    const currentDate = new Date().toISOString();

    const serRef = doc(firestore, "heroes/" + hero.name + "/series/" + formattedTitle);
    promises.push(setDoc(serRef, {
      title: formattedTitle,
      data: seriesData,
      modified: currentDate
    }));

    const userSerData = doc(firestore, "users/" + userEmail + "/heroes/" + hero.name + "/series/" + formattedTitle);
    promises.push(setDoc(userSerData, {
      title: formattedTitle,
      data: seriesData,
      modified: currentDate
    }));

    const docRef = doc(firestore, "heroes/" + hero.name + "/series/" + formattedTitle + "/comics/" + issueNumber);
    promises.push(setDoc(docRef, {
      issue: issueNumber,
      title: mainComic.title,
      data: { ...mainComic, modified: currentDate },
      variants: issueData.variants,
      modified: currentDate
    }));

    const userData = doc(firestore, "users/" + userEmail + "/heroes/" + hero.name + "/series/" + formattedTitle + "/comics/" + issueNumber);
    promises.push(setDoc(userData, {
      issue: issueNumber,
      title: mainComic.title,
      data: { ...mainComic, modified: currentDate },
      variants: issueData.variants,
      modified: currentDate
    }));
  });

  await Promise.all(promises);
};

/**
 * Update series in Firebase
 */
export const updateSeriesInFirebase = async (series, hero, userEmail) => {
  const formattedTitle = sanitizeSeriesTitle(formatSeriesTitle(series.title, series.startYear));
  const seriesData = { ...series, title: formattedTitle };

  const serRef = doc(firestore, "heroes/" + hero.name + "/series/" + formattedTitle);
  await setDoc(serRef, { title: formattedTitle, data: seriesData });

  const userSerData = doc(firestore, "users/" + userEmail + "/heroes/" + hero.name + "/series/" + formattedTitle);
  await setDoc(userSerData, { title: formattedTitle, data: seriesData });
};

/**
 * Update the modified date on a series document (used when a comic is marked as owned)
 */
export const updateSeriesModifiedDate = async (heroName, seriesTitle, userEmail) => {
  const modified = new Date().toISOString();
  const pathTitle = sanitizeSeriesTitle(seriesTitle);
  const userSerData = doc(firestore, "users/" + userEmail + "/heroes/" + heroName + "/series/" + pathTitle);
  await setDoc(userSerData, { modified }, { merge: true });
};

/**
 * Add a single comic issue to an existing tracked series in Firebase.
 * Writes to both the global and user-specific series comics subcollections.
 */
export const addComicToTrackedSeries = async (comic, heroName, seriesTitle, userEmail) => {
  const issueNumber = String(comic.issueNumber);
  const modified = new Date().toISOString();
  const comicData = {
    issue: issueNumber,
    title: comic.title,
    data: { ...comic, modified },
    variants: [],
    modified
  };

  const globalRef = doc(firestore, "heroes/" + heroName + "/series/" + seriesTitle + "/comics/" + issueNumber);
  const userRef = doc(firestore, "users/" + userEmail + "/heroes/" + heroName + "/series/" + seriesTitle + "/comics/" + issueNumber);

  await Promise.all([
    setDoc(globalRef, comicData),
    setDoc(userRef, comicData)
  ]);
};
